import React, { ImgHTMLAttributes, ReactNode } from 'react'

import Link from 'next/link'

import { Heading6 } from '~/theme/utils/typography'
import {
  CheckoutFlow,
  DailyOrFestivalReservation,
  EventReservation,
  SuccessPageReservation,
  useCheckoutContext,
} from '~/utils/checkoutFlow'
import { getLogos } from '~/utils/helpers'
import { useReleaseSlot } from '~/utils/hooks/useReleaseSlot'
import { isDaily, isFER } from '~/utils/isReservableType'

import { CheckoutReservation } from '~/components/Checkout/CheckoutPage/CheckoutPageTypes'
import { paths } from '~/constants/paths'
import { FestivalCodesEnum } from '~/generated/graphql'
import { gt } from '~/locale'
import { VisibleFrom } from '~/shared/atoms/VisibleFrom'

import { BackButton, BackButtonIcon, Container, Content, Logo, Wrapper } from './CheckoutHeaderShards'

type CheckoutHeaderProps = {
  children: ReactNode
  subheader?: ReactNode
}

export const CheckoutHeaderView = ({ children, subheader }: CheckoutHeaderProps) => {
  return (
    <Container>
      <Wrapper>
        <Content>{children}</Content>
      </Wrapper>
      {subheader}
    </Container>
  )
}

export const getFERLandingPath = (code: FestivalCodesEnum) => {
  switch (code) {
    case 'rw':
      return paths.festivalListing('restaurant-week')
    case 'bw':
      return paths.festivalListing('breakfast-week')
    default:
      return paths.home
  }
}

export const getCheckoutLogoProps = (reservation: CheckoutReservation | SuccessPageReservation, flow?: CheckoutFlow) => {
  let logoUrl = getLogos('club').centered
  let logoPath = paths.home
  let size: 'regular' | 'big' = 'regular'

  if ('reservable' in reservation && reservation.reservable) {
    const festivalCode = isFER(reservation.reservable) && reservation.reservable.festivalEdition.festival.code

    logoUrl = isFER(reservation.reservable) ? getLogos(festivalCode as FestivalCodesEnum).centered : getLogos('club').centered

    if (isFER(reservation.reservable)) {
      logoPath = getFERLandingPath(festivalCode as FestivalCodesEnum)
    }
    if (isDaily(reservation.reservable) && flow === CheckoutFlow.CHM) {
      logoPath = paths.chefsMenuOffers
    }

    if (festivalCode === FestivalCodesEnum.Bw) {
      size = 'big'
    }
  }

  return {
    logoPath,
    logoUrl,
    size,
  }
}

export const CheckoutHeaderLogo = (props: ImgHTMLAttributes<HTMLImageElement>) => {
  const { reservation, flow } = useCheckoutContext<DailyOrFestivalReservation | EventReservation>()
  const { logoPath, logoUrl, size } = getCheckoutLogoProps(reservation, flow)

  const releaseSlot = useReleaseSlot()

  return <CheckoutHeaderLogoPicture logoPath={logoPath} logoUrl={logoUrl} size={size} onClick={releaseSlot} {...props} />
}

export const CheckoutHeaderLogoPicture = ({
  logoUrl,
  logoPath,
  ...props
}: ImgHTMLAttributes<HTMLImageElement> & { logoPath: string; logoUrl: string; size: 'regular' | 'big' }) => {
  return (
    <Link href={logoPath} passHref>
      <a>
        <picture>
          <source srcSet={logoUrl} />
          <Logo src={logoUrl} {...props} />
        </picture>
      </a>
    </Link>
  )
}

type CheckoutHeaderBackButtonProps = {
  onClick: React.MouseEventHandler<HTMLButtonElement>
}

export const CheckoutHeaderBackButton = ({ onClick }: CheckoutHeaderBackButtonProps) => {
  return (
    <BackButton id='back-button' onClick={onClick}>
      <BackButtonIcon icon='arrow-short' />
      <VisibleFrom breakpoint='m'>
        <Heading6>{gt.tp('CheckoutHeader', 'Back')}</Heading6>
      </VisibleFrom>
    </BackButton>
  )
}
